'use client';
'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class WindowService {
  static get document() {
    return window.document;
  }
  static get window() {
    return window;
  }
  static createDynamicScript(args) {
    const script = WindowService.document.createElement('script');
    const {
      id,
      src,
      async,
      onload,
      onerror
    } = args;
    script.src = src;
    script.async = async !== null && async !== void 0 ? async : true;
    if (id) script.id = id;
    if (onload) script.onload = onload;
    if (onerror) script.onerror = onerror;
    WindowService.document.body.appendChild(script);
  }
}

class CollectorStub {
  constructor(options, callbacks) {
    this.invoked = false;
    this.ready = false;
    this.options = options;
    this.callbacks = callbacks;
    this.sessionData = Object.assign({}, options.sessionData);
  }
  track(event, properties, options) {
    // noop
  }
  page(properties, options) {
    // noop
  }
  identify(userId, properties, options) {
    // noop
  }
}

const normalizePageData = data => {
  return Object.assign({
    featuresPresent: []
  }, data);
};

const normalizeSessionData = userContextResponse => {
  var _a;
  const {
    userContextData
  } = userContextResponse || {};
  const newContextData = Object.assign(Object.assign({}, userContextData), (_a = userContextData === null || userContextData === void 0 ? void 0 : userContextData.additionalMetaData) !== null && _a !== void 0 ? _a : {});
  // @ts-expect-error Skip check for this property
  delete newContextData.additionalMetaData;
  return newContextData;
};

const safelyParseJson = input => {
  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch (_) {
      // never throw
    }
  }
};

const DEFAULT_CONFIG = 'sfpa.analytics.public.collector.default_config';
const PAGE_TYPE_CONFIG = 'sfpa.analytics.public.collector.page_type_config';
/**
 * Get Collector configuration from user-context payload
 */
const getCollectorConfig = (pageType, features) => {
  var _a;
  const defaultConfig = (_a = safelyParseJson(features[DEFAULT_CONFIG])) !== null && _a !== void 0 ? _a : {};
  const pageTypeConfigs = safelyParseJson(features[PAGE_TYPE_CONFIG]);
  if (features[DEFAULT_CONFIG] && !defaultConfig) {
    console.warn('Detected invalid JSON in Collector config');
  }
  let pageTypeConfig = {};
  if (typeof pageType === 'string' && pageTypeConfigs) {
    const lowerPageType = pageType.toLowerCase();
    if (pageTypeConfigs[lowerPageType]) pageTypeConfig = pageTypeConfigs[lowerPageType];
  }
  return Object.assign(Object.assign({}, defaultConfig), pageTypeConfig);
};

const ENVIRONMENTS = ['prd', 'stg', 'qat', 'dev'];
const SERVER_ALLOWED_PASSTHROUGH_REQUEST_HEADERS = new Set(['x-request-id', 'x-chewy-auth-action-token', 'x-aka-secret', 'x-forwarded-for', 'x-stack-id', 'user-agent', 'cookie', 'pragma', 'x-chewy-internal', 'true-client-ip', 'Referer', 'akamai-bot', 'akamai-bot-sbsd', 'x-kasada-request-id', 'x-kasada-client-id', 'x-kasada-classification', 'x-akamai-edgescape', 'nma-linker-id', 'nmaLinkerId']);
const BROWSER_ALLOWED_PASSTHROUGH_REQUEST_HEADERS = new Set(['x-chewy-internal', 'nma-linker-id', 'nmaLinkerId']);

const getClientEndpoint = (pageType, environment) => {
  if (!ENVIRONMENTS.includes(environment)) {
    throw new Error('Invalid environment');
  }
  const host = environment === 'prd' ? 'www.chewy.com' : `www-${environment}.chewy.net`;
  return `https://${host}/app/rest/user-context?pageType=${pageType}&lib=ale-client`;
};

const getPassThroughRequestHeaders = (incomingHttpHeaders, clientSideAllowedHeadersOnly = false) => {
  const headers = {};
  if (!incomingHttpHeaders) return headers;
  const FILTER = clientSideAllowedHeadersOnly ? BROWSER_ALLOWED_PASSTHROUGH_REQUEST_HEADERS : SERVER_ALLOWED_PASSTHROUGH_REQUEST_HEADERS;
  for (const key of FILTER) {
    const value = incomingHttpHeaders[key];
    if (typeof value === 'string') headers[key] = value;
  }
  return headers;
};

const getSessionData = _a => __awaiter(void 0, [_a], void 0, function* ({
  pageType,
  environment,
  serviceEndpoint,
  incomingHttpHeaders
}) {
  const requestOptions = {};
  if (typeof window !== 'undefined') {
    if (incomingHttpHeaders) {
      requestOptions.headers = getPassThroughRequestHeaders(incomingHttpHeaders, true);
    }
    if (!serviceEndpoint) {
      serviceEndpoint = getClientEndpoint(pageType, environment);
    }
  } else {
    console.error('Unable to fetch session data: Not in a browser environment');
    return null;
  }
  try {
    const response = yield fetch(serviceEndpoint, requestOptions);
    if (!response.ok) {
      return null; // Or you could throw an error or return a custom error object
    }
    return response;
  } catch (error) {
    return null;
  }
});

class CollectorService {
  // Ensure we have a queue on the window object
  static initializeQueue() {
    if (!WindowService.window.collectorQueue) {
      WindowService.window.collectorQueue = [];
    }
  }
  constructor() {
    // Private constructor to prevent direct instantiation
  }
  static enqueueEventEmitter(eventEmitter) {
    CollectorService.initializeQueue();
    WindowService.window.collectorQueue.push(eventEmitter);
  }
  static emitSafely(eventEmitter) {
    try {
      eventEmitter();
    } catch (error) {
      console.error('Failed to emit analytics event.', error);
    }
  }
  static flushQueue() {
    const queue = WindowService.window.collectorQueue || [];
    if (queue.length > 0) {
      queue.forEach(emitter => this.emitSafely(emitter));
      WindowService.window.collectorQueue = [];
    }
  }
  static emitEvent(eventEmitter) {
    var _a;
    if ((_a = WindowService.window.collector) === null || _a === void 0 ? void 0 : _a.ready) {
      CollectorService.flushQueue();
      CollectorService.emitSafely(eventEmitter);
    } else {
      CollectorService.enqueueEventEmitter(eventEmitter);
    }
  }
  static track(eventName, properties, options) {
    CollectorService.emitEvent(() => {
      var _a;
      (_a = WindowService.window.collector) === null || _a === void 0 ? void 0 : _a.track(eventName, properties, options);
    });
  }
  static page(properties, options) {
    CollectorService.emitEvent(() => {
      var _a;
      (_a = WindowService.window.collector) === null || _a === void 0 ? void 0 : _a.page(properties, options);
    });
  }
  static identify(userId, properties, options) {
    CollectorService.emitEvent(() => {
      var _a;
      (_a = WindowService.window.collector) === null || _a === void 0 ? void 0 : _a.identify(userId, properties, options);
    });
  }
}

/**
 * Handles asynchronous, failure-tolerant initialization of Analytics.
 */
const useInitAnalytics = ({
  appName,
  appVersion,
  pageType,
  pageData,
  environment,
  region,
  automaticPageViews = false
}) => {
  const loadedRef = react.useRef(false);
  const fetchStartedRef = react.useRef(false);
  const [sessionDataReady, setSessionDataReady] = react.useState(false);
  const [sessionData, setSessionData] = react.useState(null);
  const [collectorConfig, setCollectorConfig] = react.useState(null);
  /**
   * Acquire session data
   */
  react.useEffect(() => {
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    getSessionData({
      pageType,
      environment
    }).then(res => __awaiter(void 0, void 0, void 0, function* () {
      if (res && res.ok) {
        try {
          const json = yield res.json();
          const sessionData = normalizeSessionData(json);
          const collectorConfig = getCollectorConfig(pageType, json.analyticsToggles);
          if (sessionData && collectorConfig) {
            setSessionData(sessionData);
            setCollectorConfig(collectorConfig);
            setSessionDataReady(true);
          }
        } catch (error) {
          console.error('Error processing session data:', error);
        }
      } else {
        console.warn('Failed to fetch session data or response not OK:', res === null || res === void 0 ? void 0 : res.status, res === null || res === void 0 ? void 0 : res.statusText);
      }
    })).catch(error => {
      console.error('Fetch operation failed:', error);
    });
  }, [setSessionDataReady, setSessionData, setCollectorConfig, pageType, environment]);
  /**
   * Initialize Collector once session data is ready
   */
  react.useEffect(() => {
    if (!loadedRef.current && sessionDataReady && collectorConfig) {
      const mergedPageData = Object.assign(Object.assign({}, pageData !== null && pageData !== void 0 ? pageData : {}), {
        app: {
          name: appName,
          version: appVersion
        },
        pageType,
        environment,
        region
      });
      const {
        platforms,
        url
      } = collectorConfig;
      if (platforms && url) {
        const combinedSessionData = Object.assign(Object.assign({}, sessionData), normalizePageData(mergedPageData));
        const collector = WindowService.window.collector || new CollectorStub({
          appName,
          appVersion,
          platforms,
          sessionData: combinedSessionData,
          automaticPageViews
        }, {
          onload: () => {
            CollectorService.flushQueue();
          },
          onerror: () => {
            console.error('Failed to initialize Collector', sessionData);
          }
        });
        if (collector.ready || collector.invoked) return;
        collector.invoked = true;
        WindowService.window.collector = collector;
        WindowService.createDynamicScript({
          src: url
        });
      } else {
        console.error('Missing collector config');
      }
      loadedRef.current = true;
    }
  }, [sessionDataReady, sessionData, collectorConfig, appName, appVersion, automaticPageViews, environment, region, pageData, pageType]);
};

/**
 * @deprecated
 */
const AnalyticsProvider = props => {
  const {
      children
    } = props,
    passThruProps = __rest(props, ["children"]);
  useInitAnalytics(passThruProps);
  return jsxRuntime.jsx(jsxRuntime.Fragment, {
    children: children
  });
};
AnalyticsProvider.displayName = 'AnalyticsProvider';

/**
 * This component initializes analytics, fetches required session data, and publishes
 * events to supported analytics platforms.
 * @deprecated since 1.30.0 - Use the {@link useInitAnalytics} custom hook instead
 */
const Analytics = props => {
  const {
      children
    } = props,
    passThruProps = __rest(props, ["children"]);
  return jsxRuntime.jsx(AnalyticsProvider, Object.assign({}, passThruProps, {
    children: children
  }));
};
Analytics.displayName = 'Analytics';

/**
 * Accepts a function that contains imperative, possibly effectful code.
 *
 * @param effect Imperative function that can return a cleanup function
 * @returns
 */
// eslint-disable-next-line react-hooks/exhaustive-deps
const useEffectOnce = effect => react.useEffect(effect, []);

/**
 * The track function may be safely called in any context to emit an event.
 */
const useTrack = () => {
  return CollectorService.track;
};
/**
 * The page function may be safely called in any context to emit a page view.
 * It is not necessary to use this if the <Analytics> component was initialized
 * with the `automaticPageViews` option.
 */
const usePage = () => {
  return CollectorService.page;
};
/**
 * The identify function may be safely called in any context. It should not be
 * necessary to use this as identify messages are automatically instrumented.
 */
const useIdentify = () => {
  return CollectorService.identify;
};

const DEFAULT_THRESHOLD = 0.5;
/**
 * Component that wraps another component and reacts to viewport visibility and clicks.
 */
const TrackedElement = _a => {
  var _b, _c, _d, _e;
  var {
      listeners = {},
      properties = {},
      wrapperElement = 'div',
      children
    } = _a,
    props = __rest(_a, ["listeners", "properties", "wrapperElement", "children"]);
  const parentElemRef = react.useRef(null);
  const observerSetRef = react.useRef(false);
  const [observer, setObserver] = react.useState(null);
  const track = useTrack();
  let wasVisible = false;
  let threshold = (_c = (_b = listeners.onVisibleOnce) === null || _b === void 0 ? void 0 : _b.threshold) !== null && _c !== void 0 ? _c : DEFAULT_THRESHOLD;
  if (threshold < 0) threshold = 0;else if (threshold > 1) threshold = 1;
  const thresholdArr = threshold > 0 && threshold < 1 ? threshold : [0, 1];
  const visibleOnceEventName = (_d = listeners.onVisibleOnce) === null || _d === void 0 ? void 0 : _d.event;
  const wrapperProps = Object.assign({}, props);
  if ((_e = listeners.onClick) === null || _e === void 0 ? void 0 : _e.event) {
    const clickEventName = listeners.onClick.event;
    wrapperProps.onClick = e => {
      track(clickEventName, properties);
      (props === null || props === void 0 ? void 0 : props.onClick) && props.onClick(e);
    };
  }
  const onEnter = () => {
    if (!wasVisible && visibleOnceEventName) {
      track(visibleOnceEventName, properties);
      wasVisible = true;
    }
  };
  const dispatchEvents = entries => {
    const entry = entries[0];
    const withinThreshold = entry.intersectionRatio >= threshold;
    if (withinThreshold) {
      onEnter();
    }
  };
  useEffectOnce(() => {
    setObserver(new IntersectionObserver(dispatchEvents, {
      threshold: thresholdArr
    }));
    return () => {
      observer === null || observer === void 0 ? void 0 : observer.disconnect();
    };
  });
  react.useEffect(() => {
    if (!observerSetRef.current && parentElemRef.current && observer) {
      observer.observe(parentElemRef.current);
      observerSetRef.current = true;
    }
  });
  return /*#__PURE__*/react.createElement(wrapperElement, Object.assign({
    ref: parentElemRef
  }, wrapperProps), children);
};
TrackedElement.displayName = 'TrackedElement';

exports.Analytics = Analytics;
exports.TrackedElement = TrackedElement;
exports.useIdentify = useIdentify;
exports.useInitAnalytics = useInitAnalytics;
exports.usePage = usePage;
exports.useTrack = useTrack;
